<template>
    <div class="dropdown-select">
        <!-- Call to action div with selected label -->
        <div
            class="dropdown-select__trigger p-sm display-flex align-items-center justify-between flex-wrap"
            @click="toggleDropdown"
        >
            <p class="dropdown-select__selected text-moat-600 bold text-truncate w-100">
                {{ selectedOption ? selectedOption.label : placeholder }}
            </p>
            <p class="description text-stone-600 small">
                {{ selectedOption ? selectedOption.description : '' }}
            </p>
            <font-awesome-icon
                class="dropdown-select__chevron text-secondary"
                icon="fas fa-chevron-down"
                size="sm"
                :class="{ 'dropdown-select__chevron--rotated': isOpen }"
            />
        </div>

        <transition name="dropdown-fade">
            <div
                v-if="isOpen"
                class="dropdown-select__options"
                v-click-outside="
                    () => {
                        if (isOpen) {
                            isOpen = false
                        }
                    }
                "
            >
                <div
                    v-for="(option, index) in options"
                    :key="index"
                    class="dropdown-select__option px-xs display-flex align-items-center"
                    @click="selectOption(option)"
                >
                    <div
                        class="dropdown-select__option-inner display-flex flex-wrap align-items-center p-sm w-100"
                        :class="{
                            'dropdown-select__option-inner--selected': isOptionSelected(option),
                        }"
                    >
                        <p class="dropdown-select__selected text-moat-600 bold text-truncate w-100">
                            {{ option.label }}
                        </p>
                        <p class="description text-stone-600 small">
                            {{ option.description }}
                        </p>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, watch, computed, defineEmits } from 'vue'

const props = defineProps({
    options: {
        type: Array,
        default: () => [],
    },
    modelValue: {
        type: [String, Number, Object],
        default: null,
    },
    placeholder: {
        type: String,
        default: 'Select an option',
    },
})

const emit = defineEmits(['update:modelValue', 'change'])

const isOpen = ref(false)
const selectedOption = ref(null)

// Find the initially selected option based on modelValue
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue) {
            selectedOption.value = props.options.find(
                (option) => option.value === newValue || option === newValue
            )
        } else {
            selectedOption.value = null
        }
    },
    { immediate: true }
)

// Toggle dropdown visibility
const toggleDropdown = () => {
    isOpen.value = !isOpen.value
}

const isOptionSelected = (option) => {
    if (!selectedOption.value) return false

    return option.value !== undefined
        ? option.value === selectedOption.value.value
        : option === selectedOption.value
}

// Handle option selection
const selectOption = (option) => {
    selectedOption.value = option
    isOpen.value = false

    // Emit the selected value (support for v-model)
    emit('update:modelValue', option.value !== undefined ? option.value : option)

    // Also emit a change event with the full option object
    emit('change', option)
}
</script>

<style scoped lang="scss">
@forward '@/styles/new_styles.scss';

.dropdown-select {
    position: relative;
    width: 100%;
    max-width: 400px;

    &__trigger {
        border-radius: var(--border-radius-lg);
        cursor: pointer;
        background: var(--color-stone-0);
        border: 1px solid var(--color-stone-300);
    }

    &__chevron {
        position: absolute;
        right: 10px;
        transition: transform 0.3s ease-in-out;

        &--rotated {
            transform: rotate(180deg);
        }
    }

    &__options {
        position: absolute;
        top: calc(100% + 10px);
        left: 0;
        width: 100%;
        overflow-y: auto;
        background-color: var(--color-background-panel);
        border-radius: var(--border-radius-lg);
        z-index: 10;
        box-shadow: var(--shadow-sm);
        padding-top: var(--spacing-sm);
    }

    &__option {
        padding-bottom: var(--spacing-sm);
    }

    &__option-inner {
        cursor: pointer;
        border-radius: var(--border-radius-lg);
        transition: all 0.3s ease-in-out;

        &:hover {
            background-color: var(--color-moat-100);
        }

        &--selected {
            background-color: var(--color-moat-100);
        }
    }

    &__check {
        min-width: 25px;
    }

    .dropdown-fade-enter-active,
    .dropdown-fade-leave-active {
        transition:
            opacity 0.3s,
            transform 0.3s;
    }

    .dropdown-fade-enter-from,
    .dropdown-fade-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
}
</style>
